import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import toast from 'react-hot-toast';
import { useRBAC } from '../hooks/useRBAC';

const STATUS_BADGES = {
  new: 'bg-accent/10 text-accent border-accent/20',
  contacted: 'bg-purple-100 text-purple-700 border-purple-200',
  qualified: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  proposal_sent: 'bg-pink-50 text-pink-700 border-pink-200',
  won: 'bg-primary/10 text-primary border-primary/20',
  lost: 'bg-destructive-light text-destructive border-destructive/20',
};
const SOURCE_ICONS = { attom_scanner: 'fa-satellite-dish', daily_auto_scan: 'fa-clock', permit_scraper: 'fa-file-contract', google_solar: 'fa-sun', smart_search: 'fa-magnifying-glass', live_scan: 'fa-tower-broadcast', referral: 'fa-handshake', csv_import: 'fa-file-csv', autopilot: 'fa-robot', manual: 'fa-pen-to-square' };
const SOLAR_BADGE = { High: 'bg-primary/10 text-primary', Medium: 'bg-yellow-50 text-yellow-700', Low: 'bg-destructive-light text-destructive' };

// Small avatar chip showing lead owner
function OwnerChip({ name, email, isAdmin, onReassign }) {
  if (!name && !email) {
    return (
      <span
        className={`flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 ${isAdmin ? 'cursor-pointer hover:bg-amber-50 hover:text-amber-600' : ''}`}
        onClick={isAdmin ? onReassign : undefined}
        title={isAdmin ? 'Unassigned — click to assign' : 'Unassigned'}
      >
        <i className="fa-solid fa-user-slash text-[8px]" />Unassigned
      </span>
    );
  }
  const initials = (name || email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <span
      className={`flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/8 text-primary ${isAdmin ? 'cursor-pointer hover:bg-primary/15' : ''}`}
      onClick={isAdmin ? onReassign : undefined}
      title={isAdmin ? `${name || email} — click to reassign` : name || email}
    >
      <span className="w-3 h-3 rounded-full bg-primary text-white flex items-center justify-center text-[7px] font-bold">{initials}</span>
      {name || email}
      {isAdmin && <i className="fa-solid fa-pen text-[7px] opacity-60" />}
    </span>
  );
}

export default function LeadDatabase() {
  const navigate = useNavigate();
  const { can, isAdmin } = useRBAC();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState(() => new URLSearchParams(window.location.search).get('source') || '');
  const [ownerFilter, setOwnerFilter] = useState('');   // admin: filter by rep
  const [stateFilter, setStateFilter] = useState('');   // location: filter by state
  const [cityFilter, setCityFilter] = useState('');     // location: filter by city
  const [locations, setLocations] = useState({ states: [], cities: [] });
  const [reps, setReps] = useState([]);                 // admin: list of reps
  const [showCreate, setShowCreate] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', address: '', city: '', state: 'CA', zip: '', estimated_value: 0 });
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [assignModal, setAssignModal] = useState(null); // { lead } for reassign modal
  const [assignTarget, setAssignTarget] = useState('');
  const fileInputRef = React.useRef(null);

  // Load reps list for admin filter
  useEffect(() => {
    if (isAdmin) {
      api.get('/api/users/reps').then(r => setReps(r.data.reps || [])).catch(() => {});
    }
  }, [isAdmin]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort_by: 'score', sort_order: 'desc' };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (ownerFilter) params.owner_email = ownerFilter;
      if (stateFilter) params.state = stateFilter;
      if (cityFilter) params.city = cityFilter;
      const res = await api.get('/api/leads', { params });
      setLeads(res.data.leads); setTotal(res.data.total); setPages(res.data.pages);
    } catch { toast.error('Failed to load leads'); }
    setLoading(false);
  }, [page, search, statusFilter, sourceFilter, ownerFilter, stateFilter, cityFilter]);

  useEffect(() => {
    api.get('/api/leads/locations').then(r => setLocations(r.data || { states: [], cities: [] })).catch(() => {});
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/leads', newLead);
      toast.success('Lead created!');
      setShowCreate(false);
      setNewLead({ name: '', email: '', phone: '', address: '', city: '', state: 'CA', zip: '', estimated_value: 0 });
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAssign = async () => {
    if (!assignModal || !assignTarget) return;
    try {
      await api.patch(`/api/leads/${assignModal.id}/assign`, null, { params: { owner_email: assignTarget } });
      toast.success('Lead reassigned!');
      setAssignModal(null);
      setAssignTarget('');
      fetchLeads();
    } catch { toast.error('Failed to reassign'); }
  };

  const scoreColor = (s) => s >= 80 ? 'text-primary' : s >= 60 ? 'text-secondary' : s >= 40 ? 'text-accent' : 'text-destructive';
  const scoreBar = (s) => s >= 80 ? 'bg-primary' : s >= 60 ? 'bg-secondary' : s >= 40 ? 'bg-accent' : 'bg-destructive';

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/api/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.imported} leads from ${res.data.filename}`);
      if (res.data.skipped > 0) toast(`${res.data.skipped} rows skipped`, { icon: 'i' });
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleBatchEnrich = async () => {
    setEnriching(true);
    try {
      const res = await api.post('/api/enrich/batch', { limit: 15 });
      toast.success(`AI enriched ${res.data.enriched} leads (${res.data.failed} failed)`);
    } catch { toast.error('Batch enrichment failed'); }
    finally { setEnriching(false); }
  };

  const [outreaching, setOutreaching] = useState(false);
  const handleBulkOutreach = async () => {
    setOutreaching(true);
    try {
      const body = { limit: 25, only_missing: true };
      if (selectedIds.length > 0) body.lead_ids = selectedIds;
      const res = await api.post('/api/leads/enrich-full-bulk', body);
      const c = res.data.channel_counts || {};
      toast.success(`Enriched ${res.data.enriched} leads · mail ${c.mail || 0} · email ${c.email || 0} · phone ${c.phone || 0}`);
      fetchLeads();
    } catch { toast.error('Bulk outreach enrichment failed'); }
    finally { setOutreaching(false); }
  };
  const openMailAsset = (kind) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${API_URL}/api/outreach/${kind}`, '_blank');
  };

  const [waterfalling, setWaterfalling] = useState(false);
  const handleWaterfall = async () => {
    setWaterfalling(true);
    try {
      const body = { limit: 50, only_owner: true };
      if (selectedIds.length > 0) body.lead_ids = selectedIds;
      else if (sourceFilter) body.source = sourceFilter;
      const res = await api.post('/api/leads/bulk-waterfall-enrich', body);
      toast.success(`Waterfall enriched ${res.data.enriched} leads · ${res.data.callable_phones} callable · ${res.data.emails_found} emails`);
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Waterfall enrichment failed'); }
    finally { setWaterfalling(false); }
  };
  const exportVerified = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${API_URL}/api/leads/export/verified-contacts.csv?only_callable=false`, '_blank');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) setSelectedIds([]);
    else setSelectedIds(leads.map(l => l.id));
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    const ids = [...selectedIds];
    try {
      if (bulkAction === 'delete') {
        if (!window.confirm(`Delete ${ids.length} leads permanently?`)) return;
        await api.post('/api/leads/bulk/delete', { lead_ids: ids });
        toast.success(`Deleted ${ids.length} leads`);
      } else {
        await api.post('/api/leads/bulk/status', { lead_ids: ids, status: bulkAction });
        toast.success(`Updated ${ids.length} leads to "${bulkAction.replace('_', ' ')}"`);
      }
      setSelectedIds([]);
      setBulkAction('');
      fetchLeads();
    } catch { toast.error('Bulk action failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-database-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Lead Database</h1>
          <p className="text-text-muted text-sm mt-1">{total} leads &middot; Sorted by lead score</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="file" accept=".csv" ref={fileInputRef} onChange={handleCsvImport} className="hidden" data-testid="csv-file-input" />
          <button onClick={() => fileInputRef.current?.click()} disabled={importing} className="btn-secondary text-xs py-2" data-testid="import-csv-btn">
            {importing ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Importing...</> : <><i className="fa-solid fa-file-import mr-1.5" />Import CSV</>}
          </button>
          <button onClick={handleBatchEnrich} disabled={enriching} className="btn-secondary text-xs py-2" data-testid="batch-enrich-btn">
            {enriching ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Enriching...</> : <><i className="fa-solid fa-brain mr-1.5" />AI Enrich All</>}
          </button>
          <button onClick={handleBulkOutreach} disabled={outreaching} className="text-xs py-2 px-3 rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="bulk-outreach-btn">
            {outreaching ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Reaching...</> : <><i className="fa-solid fa-bullhorn mr-1.5" />{selectedIds.length > 0 ? `Reach ${selectedIds.length} (Free)` : 'Enrich All (Free)'}</>}
          </button>
          <button onClick={handleWaterfall} disabled={waterfalling} className="text-xs py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="waterfall-enrich-btn">
            {waterfalling ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Enriching...</> : <><i className="fa-solid fa-water mr-1.5" />{selectedIds.length > 0 ? `Waterfall ${selectedIds.length}` : 'Waterfall Enrich'}</>}
          </button>
          <button onClick={exportVerified} className="btn-secondary text-xs py-2" data-testid="export-verified-btn"><i className="fa-solid fa-address-book mr-1.5" />Export Verified</button>
          <button onClick={() => openMailAsset('mail-merge.csv')} className="btn-secondary text-xs py-2" data-testid="mail-merge-btn"><i className="fa-solid fa-envelope-open-text mr-1.5" />Mail Merge</button>
          <button onClick={() => openMailAsset('mail-letters.pdf')} className="btn-secondary text-xs py-2" data-testid="print-letters-btn"><i className="fa-solid fa-print mr-1.5" />Print Letters</button>
          <button onClick={() => {
            const API_URL = process.env.REACT_APP_BACKEND_URL || '';
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (sourceFilter) params.append('source', sourceFilter);
            if (stateFilter) params.append('state', stateFilter);
            if (cityFilter) params.append('city', cityFilter);
            window.open(`${API_URL}/api/leads/export/csv?${params.toString()}`, '_blank');
          }} className="btn-secondary text-xs py-2" data-testid="export-csv-btn"><i className="fa-solid fa-file-export mr-1.5" />Export CSV</button>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs py-2" data-testid="create-lead-btn"><i className="fa-solid fa-plus mr-1.5" />Add Lead</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="flex-1 relative min-w-[200px]">
          <i className="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input type="text" placeholder="Search by name, email, address..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="input-field pl-10" data-testid="lead-search-input" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="input-field w-auto min-w-[140px]" data-testid="status-filter">
          <option value="">All Statuses</option>
          {['new','contacted','qualified','proposal_sent','won','lost'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }} className="input-field w-auto min-w-[140px]" data-testid="source-filter">
          <option value="">All Sources</option>
          {['attom_scanner','daily_auto_scan','permit_scanner','permit_scraper','google_solar','smart_search','live_scan','referral','csv_import','autopilot','manual','demo_pack'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        {/* Location: state + city */}
        <select value={stateFilter} onChange={e => { setStateFilter(e.target.value); setCityFilter(''); setPage(1); }} className="input-field w-auto min-w-[150px]" data-testid="state-filter">
          <option value="">All States</option>
          {locations.states.map(s2 => <option key={s2.state} value={s2.state}>{s2.state} ({s2.count})</option>)}
        </select>
        <select value={cityFilter} onChange={e => { setCityFilter(e.target.value); setPage(1); }} className="input-field w-auto min-w-[150px]" data-testid="city-filter">
          <option value="">All Cities</option>
          {locations.cities.filter(c => !stateFilter || c.state === stateFilter).map(c => <option key={`${c.city}-${c.state}`} value={c.city}>{c.city}{c.state ? `, ${c.state}` : ''} ({c.count})</option>)}
        </select>
        {(stateFilter || cityFilter) && (
          <button onClick={() => { setStateFilter(''); setCityFilter(''); setPage(1); }} className="btn-secondary text-xs py-2" data-testid="clear-location-filter"><i className="fa-solid fa-xmark mr-1.5" />Clear location</button>
        )}
        {/* Admin-only: filter by rep */}
        {isAdmin && reps.length > 0 && (
          <select
            value={ownerFilter}
            onChange={e => { setOwnerFilter(e.target.value); setPage(1); }}
            className="input-field w-auto min-w-[160px]"
            data-testid="rep-filter"
          >
            <option value="">All Reps</option>
            <option value="__unassigned__">Unassigned</option>
            {reps.map(r => <option key={r.email} value={r.email}>{r.name} ({r.lead_count})</option>)}
          </select>
        )}
      </div>

      {/* Ownership scope banner for salesperson */}
      {!isAdmin && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/10 text-xs font-medium text-primary" data-testid="ownership-scope-banner">
          <i className="fa-solid fa-user-check" />Showing your assigned leads only
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 px-5 py-3 rounded-2xl bg-primary/5 border border-primary/20 animate-slide-down" data-testid="bulk-actions-bar">
          <span className="text-sm font-semibold text-primary"><i className="fa-solid fa-check-double mr-2" />{selectedIds.length} selected</span>
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} className="input-field w-auto min-w-[160px] h-9 text-xs" data-testid="bulk-action-select">
            <option value="">Choose action...</option>
            <option value="contacted">Mark Contacted</option>
            <option value="qualified">Mark Qualified</option>
            <option value="proposal_sent">Mark Proposal Sent</option>
            <option value="won">Mark Won</option>
            <option value="lost">Mark Lost</option>
            {can('bulk_delete_leads') && <option value="delete">Delete Selected</option>}
          </select>
          <button onClick={handleBulkAction} disabled={!bulkAction} className="btn-primary text-xs py-1.5 disabled:opacity-40" data-testid="bulk-apply-btn">Apply</button>
          <button onClick={() => { setSelectedIds([]); setBulkAction(''); }} className="text-xs text-text-muted hover:text-text-primary transition-colors" data-testid="bulk-clear-btn">Clear selection</button>
        </div>
      )}

      {/* Table */}
      <div className="card-interactive overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3.5 text-left bg-surface-alt w-10">
                  <input type="checkbox" checked={selectedIds.length === leads.length && leads.length > 0} onChange={toggleSelectAll} className="checkbox-custom" data-testid="select-all-checkbox" />
                </th>
                {['Lead','Location','Status','Score','Solar','Source','Est. Value', isAdmin ? 'Owner' : '', ''].map(h => (
                  <th key={h} className="px-4 py-3.5 text-left text-[10px] font-bold uppercase tracking-widest text-text-muted bg-surface-alt">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-20 text-center text-text-muted"><i className="fa-solid fa-spinner fa-spin text-lg" /></td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={isAdmin ? 10 : 9} className="py-20 text-center text-text-muted">No leads found</td></tr>
              ) : leads.map((lead, i) => (
                <tr key={lead.id} className={`border-b border-border/50 hover:bg-surface-alt/50 transition-colors cursor-pointer animate-fade-in ${selectedIds.includes(lead.id) ? 'bg-primary/5' : ''}`} style={{ animationDelay: `${i * 25}ms` }} data-testid={`lead-row-${lead.id}`}>
                  <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.includes(lead.id)} onChange={() => toggleSelect(lead.id)} className="checkbox-custom" data-testid={`select-lead-${lead.id}`} />
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">{lead.name?.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">{lead.name}</p>
                        <p className="text-[11px] text-text-muted">{lead.email || lead.phone || (lead.email_guess ? `${lead.email_guess} · guess` : '')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <p className="text-sm text-text-primary">{lead.city}</p>
                    <p className="text-[11px] text-text-muted">{lead.state} {lead.zip}</p>
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <span className={`badge capitalize ${STATUS_BADGES[lead.status] || 'bg-muted text-text-muted'}`}>{(lead.status || '').replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-bold text-sm tabular-nums ${scoreColor(lead.score)}`}>{lead.score}</span>
                      <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                        <div className={`h-full rounded-full transition-all duration-500 ${scoreBar(lead.score)}`} style={{ width: `${lead.score}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${SOLAR_BADGE[lead.solar_potential] || 'bg-muted text-text-muted'}`}>{lead.solar_potential || 'N/A'}</span>
                    {(lead.panel_count > 0 || lead.estimated_savings > 0) && (
                      <p className="text-[10px] text-text-muted mt-1 whitespace-nowrap font-medium" data-testid={`rooftop-info-${lead.id}`}>
                        {lead.panel_count > 0 && <span><i className="fa-solid fa-solar-panel text-amber-500 mr-1" />{lead.panel_count} panels</span>}
                        {lead.panel_count > 0 && lead.estimated_savings > 0 && <span className="mx-1">·</span>}
                        {lead.estimated_savings > 0 && <span className="text-emerald-600 font-semibold">${Math.round(lead.estimated_savings).toLocaleString()}/yr</span>}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <span className="text-xs text-text-muted flex items-center gap-1.5 capitalize"><i className={`fa-solid ${SOURCE_ICONS[lead.source] || 'fa-circle'} text-[10px]`} />{(lead.source || '').replace('_', ' ')}{lead.is_demo && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold uppercase not-italic" data-testid="demo-badge">demo</span>}</span>
                  </td>
                  <td className="px-4 py-3.5 font-mono text-sm font-semibold text-text-primary tabular-nums" onClick={() => navigate(`/leads/${lead.id}`)}>${(lead.estimated_value || 0).toLocaleString()}</td>
                  {/* Owner chip — admin only */}
                  {isAdmin && (
                    <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                      <OwnerChip
                        name={lead.assigned_to}
                        email={lead.owner_email}
                        isAdmin={isAdmin}
                        onReassign={() => { setAssignModal(lead); setAssignTarget(lead.owner_email || ''); }}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3.5">
                    <button onClick={e => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }} className="p-1.5 rounded-lg text-text-muted hover:text-primary hover:bg-primary/5 transition-all" data-testid={`view-lead-${lead.id}`}>
                      <i className="fa-solid fa-chevron-right text-xs" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface-alt">
            <p className="text-xs text-text-muted">Showing page {page} of {pages} ({total} total)</p>
            <div className="flex gap-1.5">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg text-xs border border-border text-text-secondary bg-surface hover:bg-surface-alt disabled:opacity-30 transition-all" data-testid="prev-page-btn">Previous</button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg text-xs border border-border text-text-secondary bg-surface hover:bg-surface-alt disabled:opacity-30 transition-all" data-testid="next-page-btn">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)} data-testid="create-lead-modal">
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg animate-scale-in" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">Add New Lead</h2>
            <p className="text-sm text-text-muted mb-5">Enter lead information to add to database</p>
            <form onSubmit={handleCreate} className="space-y-3">
              <input type="text" required placeholder="Full Name *" value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})} className="input-field" data-testid="new-lead-name" />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" placeholder="Email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} className="input-field" data-testid="new-lead-email" />
                <input type="tel" placeholder="Phone" value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} className="input-field" data-testid="new-lead-phone" />
              </div>
              <input type="text" placeholder="Street Address" value={newLead.address} onChange={e => setNewLead({...newLead, address: e.target.value})} className="input-field" data-testid="new-lead-address" />
              <div className="grid grid-cols-3 gap-3">
                <input type="text" placeholder="City" value={newLead.city} onChange={e => setNewLead({...newLead, city: e.target.value})} className="input-field" data-testid="new-lead-city" />
                <input type="text" placeholder="State" value={newLead.state} onChange={e => setNewLead({...newLead, state: e.target.value})} className="input-field" data-testid="new-lead-state" />
                <input type="text" placeholder="ZIP" value={newLead.zip} onChange={e => setNewLead({...newLead, zip: e.target.value})} className="input-field" data-testid="new-lead-zip" />
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-sm" data-testid="cancel-create-btn">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="submit-create-btn">Create Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reassign Modal */}
      {assignModal && isAdmin && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-6 w-full max-w-sm animate-slide-up" onClick={e => e.stopPropagation()} data-testid="reassign-modal">
            <h3 className="font-heading text-lg font-bold text-text-primary mb-1">Reassign Lead</h3>
            <p className="text-sm text-text-muted mb-4">
              Assign <strong className="text-text-primary">{assignModal.name}</strong> to a different sales rep
            </p>
            <select
              value={assignTarget}
              onChange={e => setAssignTarget(e.target.value)}
              className="input-field mb-4"
              data-testid="reassign-select"
            >
              <option value="">Unassigned</option>
              {reps.map(r => (
                <option key={r.email} value={r.email}>{r.name} ({r.role}) — {r.lead_count} leads</option>
              ))}
            </select>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setAssignModal(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={handleAssign} className="btn-primary text-sm" data-testid="confirm-reassign-btn">
                <i className="fa-solid fa-user-check mr-1.5" />Reassign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
